import { Combobox, useCombobox } from '@anyui/core';
import { SelectOption } from '@anytag/shared-types';
import { SelectOptionValue } from './types';
import { getOptionValue, getValuesArray } from './utils';
import { useSingleSelectContext } from './select-context';
import {
  SelectItem,
  SelectMenu,
  SelectSearch,
  SelectSubMenu,
  SelectTrigger,
  SelectDropdown,
  SelectSubItems,
  SelectSubTrigger
} from './components';

export const SingleSelect = <TOption extends SelectOption<string>>() => {
  const {
    setSearch,
    createPrefix,
    comboboxProps,
    hoveringOption,
    setHoveringOption,
    selectProps: { value, onChange, options = [] }
  } = useSingleSelectContext<TOption>();
  const combobox = useCombobox({
    onDropdownClose: () => {
      setSearch(''); // Reset search on close
      setHoveringOption(null); // Reset hovering option on close
    }
  });

  // Use string[] internally for comparisons
  const selectedValues = getValuesArray({ value, options, multiple: false }) as string[];

  const handleOptionSubmit = (optionValue: string) => {
    if (optionValue.startsWith(createPrefix)) {
      onChange?.(optionValue.slice(createPrefix.length) as SelectOptionValue<TOption>, 'create');
    } else {
      onChange?.(optionValue as SelectOptionValue<TOption>, 'select');
    }
    combobox.closeDropdown();
  };

  return (
    <Combobox store={combobox} onOptionSubmit={handleOptionSubmit} {...comboboxProps}>
      <SelectTrigger
        selectedValues={selectedValues}
        onClear={() => onChange?.('' as SelectOptionValue<TOption>, 'clear')}
      />

      <SelectDropdown>
        <SelectSearch selectedValues={selectedValues} />

        <SelectMenu>
          {options.map((option) => {
            const optValue = getOptionValue(option);
            const hasSubmenu = option.submenu && option.submenu.length > 0;

            if (hasSubmenu) {
              const submenuValues = option.submenu?.map((s) => getOptionValue(s)) ?? [];
              const hasSomeSubmenuSelected = !!hasSubmenu && submenuValues.some((v) => selectedValues.includes(v));

              return (
                <SelectSubMenu key={optValue} open={hoveringOption === optValue}>
                  <SelectSubTrigger option={option} active={hasSomeSubmenuSelected} />
                  <SelectSubItems option={option} selectedValues={selectedValues} onSelect={handleOptionSubmit} />
                </SelectSubMenu>
              );
            }

            return <SelectItem option={option} key={optValue} active={selectedValues.includes(optValue)} />;
          })}
        </SelectMenu>
      </SelectDropdown>
    </Combobox>
  );
};
